import type { Patient } from '@medplum/fhirtypes'
import { CommunicationMedium } from '@seen/core/models/communications/constants'
import type { ExtendedCommunication } from '@seen/core/models/communications/ExtendedCommunication'
import { SDRNotificationCommunication } from '@seen/core/models/communications/SDRNotificationCommunication'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { formatReference } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { type JSX, useMemo, useState } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { DateDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { ExtendedResourceTable } from '#src/components/fhir-table/ExtendedResourceTable.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { SDRCreateNotificationAttempt } from '#src/components/sdr/display/SDRCreateNotificationAttempt.tsx'

type Props = {
  sdr: SDR
  patient: Patient
}

export function SDRNotificationAttemptsTable({
  sdr,
  patient,
}: Props): JSX.Element {
  const [displayModal, setDisplayModal] = useState<CommunicationMedium | null>(
    null
  )
  const written = sdr.writtenNotificationAttempts

  const columns = useMemo<ColumnConfig<ExtendedCommunication>[]>(
    () => [
      {
        id: 'date',
        headerText: 'Date',
        valueType: 'date',
        filteringType: 'date-range',
        getSortFilterValue: (c) => c.sentDate,
        render: (c) => <DateDisplay value={c.sentDate} />,
        enableSorting: true,
        sortDescFirst: true,
        useAsInitialSort: true,
      },
      {
        id: 'author',
        headerText: 'Author',
        valueType: 'text',
        filteringType: 'text',
        getSortFilterValue: (c) => formatReference(c.sender),
        render: (c) => (
          <HoverPractitioner practitioner={c.practitionerSender} />
        ),
        enableSorting: true,
      },
      {
        id: 'modeOfOutreach',
        headerText: 'Mode of Outreach',
        valueType: 'text',
        filteringType: 'text',
        getSortFilterValue: (c) => c.mediumDisplay,
        render: (c) => <>{c.mediumDisplay}</>,
        enableSorting: true,
      },
      {
        id: 'note',
        headerText: 'Note',
        valueType: 'text',
        filteringType: 'text',
        getSortFilterValue: (c) => c.message,
        render: (c) => <>{c.message}</>,
        sortDescFirst: true,
        useAsInitialSort: true,
      },
    ],
    []
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center justify-between">
        <h2 className="text-lg font-semibold">Notification History</h2>
        <div className="flex flex-row items-center gap-2">
          <Button onClick={() => setDisplayModal(CommunicationMedium.VERBAL)}>
            Record Verbal Notification
          </Button>
          {written.length === 0 ? (
            <Button
              onClick={() => setDisplayModal(CommunicationMedium.WRITTEN)}
            >
              Record Written Notification
            </Button>
          ) : (
            <Button disabled>Written Notification Recorded</Button>
          )}
        </div>
      </div>

      <ExtendedResourceTable
        resourceClass={SDRNotificationCommunication}
        filterQuery={SDRNotificationCommunication.searchQuery(sdr.reference)}
        columnConfigs={columns}
      />

      {displayModal != null && (
        <Dialog open onOpenChange={() => setDisplayModal(null)}>
          <DialogContent className="p-0">
            <SDRCreateNotificationAttempt
              sdr={sdr}
              patient={patient}
              medium={displayModal}
              onCancel={() => setDisplayModal(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
